/**
 * Contact form — POSTs to /api/contact.
 * Validation mirrors the backend Pydantic constraints so we can show inline
 * errors before hitting the network.
 * Includes hidden honeypot `website` field.
 */
import { useRef, useState } from "react";
import { CheckCircle2, Loader2, Send } from "lucide-react";
import { api } from "@/lib/api";

const RULES = {
  full_name: { min: 2, max: 120, required: true, label: "Full name" },
  email: { required: true, label: "Email", pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/ },
  phone: { min: 5, max: 40, required: false, label: "Phone" },
  subject: { min: 2, max: 200, required: true, label: "Subject" },
  message: { min: 5, max: 5000, required: true, label: "Message" },
};

function validate(values) {
  const errors = {};
  Object.entries(RULES).forEach(([name, r]) => {
    const raw = values[name] ?? "";
    const v = typeof raw === "string" ? raw.trim() : "";
    if (r.required && !v) {
      errors[name] = `${r.label} is required.`;
      return;
    }
    if (!v && !r.required) return;
    if (r.min && v.length < r.min) {
      errors[name] = `${r.label} must be at least ${r.min} characters.`;
      return;
    }
    if (r.max && v.length > r.max) {
      errors[name] = `${r.label} must be at most ${r.max} characters.`;
      return;
    }
    if (r.pattern && !r.pattern.test(v)) {
      errors[name] = `Please enter a valid ${r.label.toLowerCase()}.`;
    }
  });
  return errors;
}

const initialValues = { full_name: "", email: "", phone: "", subject: "", message: "", website: "" };

export default function ContactForm({ initialSubject = "" }) {
  const [values, setValues] = useState(() => ({ ...initialValues, subject: initialSubject || "" }));
  const [touched, setTouched] = useState({});
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState(null); // 'success' | 'error'
  const [message, setMessage] = useState("");
  const formRef = useRef(null);
  const honeypotRef = useRef(null);

  const setField = (name) => (e) => {
    const value = e.target.value;
    setValues((v) => ({ ...v, [name]: value }));
    if (touched[name]) {
      const errs = validate({ ...values, [name]: value });
      setErrors((e2) => ({ ...e2, [name]: errs[name] }));
    }
  };
  const blurField = (name) => () => {
    setTouched((t) => ({ ...t, [name]: true }));
    const errs = validate(values);
    setErrors((e) => ({ ...e, [name]: errs[name] }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    // Capture honeypot from uncontrolled ref FIRST so blur-triggered re-renders
    // cannot reset it before we read.
    const hpValEarly = honeypotRef.current?.value || "";
    const errs = validate(values);
    setErrors(errs);
    setTouched(Object.fromEntries(Object.keys(RULES).map((k) => [k, true])));
    if (Object.keys(errs).length > 0) {
      // focus first errored field
      const first = Object.keys(RULES).find((k) => errs[k]);
      if (first) {
        const el = formRef.current?.querySelector(`[name="${first}"]`);
        if (el) el.focus();
      }
      return;
    }
    setSubmitting(true);
    setStatus(null);
    setMessage("");
    try {
      // Use honeypot value captured before any setState in this handler.
      const payload = {
        full_name: values.full_name.trim(),
        email: values.email.trim(),
        phone: values.phone.trim() || null,
        subject: values.subject.trim(),
        message: values.message.trim(),
        website: hpValEarly,
      };
      await api.post("/contact", payload);
      setStatus("success");
      setMessage("Thank you — we'll be in touch shortly.");
      setValues(initialValues);
      setTouched({});
      setErrors({});
    } catch (err) {
      setStatus("error");
      if (err?.response?.status >= 500 || !err?.response) {
        setMessage("Something went wrong. Please email us directly at info@mistquality.com.");
      } else if (err?.response?.status === 429) {
        setMessage("Too many submissions from this address. Please try again later.");
      } else {
        setMessage(
          "There was a problem with your submission. Please check the fields highlighted below and try again."
        );
        const detail = err?.response?.data?.detail;
        if (Array.isArray(detail)) {
          const be = {};
          for (const item of detail) {
            if (item?.field) be[item.field] = item.message;
          }
          setErrors((e) => ({ ...e, ...be }));
        }
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (status === "success") {
    return (
      <div
        data-testid="contact-success"
        role="status"
        className="bg-mist-off border border-mist-gold/40 rounded-md p-10 text-center"
      >
        <CheckCircle2 className="h-12 w-12 text-mist-gold mx-auto mb-4" aria-hidden />
        <div className="font-sans font-semibold text-2xl text-mist-navy mb-2 tracking-tight">Message received</div>
        <p className="text-sm text-mist-ink/70 max-w-md mx-auto">{message}</p>
        <button
          type="button"
          onClick={() => setStatus(null)}
          data-testid="contact-reset"
          className="mt-6 inline-flex items-center justify-center gap-2 bg-mist-navy text-white px-6 h-10 text-[11px] uppercase tracking-[0.22em] font-semibold rounded-md hover:bg-mist-navy/90 transition-all duration-200 ease-out"
        >
          Send another
        </button>
      </div>
    );
  }

  return (
    <form
      ref={formRef}
      onSubmit={onSubmit}
      data-testid="contact-form"
      noValidate
      className="bg-white border border-mist-soft rounded-md p-6 sm:p-8"
    >
      {status === "error" && (
        <div
          data-testid="contact-error"
          role="alert"
          className="mb-6 text-sm bg-red-50 text-red-800 border border-red-200 rounded-md p-3"
        >
          {message}
        </div>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <FieldInput
          name="full_name"
          label="Full name"
          placeholder="Your name"
          value={values.full_name}
          onChange={setField("full_name")}
          onBlur={blurField("full_name")}
          error={touched.full_name && errors.full_name}
          required
        />
        <FieldInput
          name="email"
          type="email"
          label="Email"
          placeholder="you@company.com"
          value={values.email}
          onChange={setField("email")}
          onBlur={blurField("email")}
          error={touched.email && errors.email}
          required
        />
        <FieldInput
          name="phone"
          label="Phone"
          placeholder="Optional"
          value={values.phone}
          onChange={setField("phone")}
          onBlur={blurField("phone")}
          error={touched.phone && errors.phone}
        />
        <FieldInput
          name="subject"
          label="Subject"
          placeholder="What is this about?"
          value={values.subject}
          onChange={setField("subject")}
          onBlur={blurField("subject")}
          error={touched.subject && errors.subject}
          required
        />
      </div>
      <div className="mt-4">
        <FieldTextarea
          name="message"
          label="Message"
          placeholder="Tell us about your inspection, logistics or supply needs."
          value={values.message}
          onChange={setField("message")}
          onBlur={blurField("message")}
          error={touched.message && errors.message}
          required
        />
      </div>
      {/* Honeypot — hidden from real users. Uncontrolled so scripted DOM sets are preserved. */}
      <div className="hidden" aria-hidden>
        <label htmlFor="website">Website</label>
        <input
          name="website"
          id="website"
          type="text"
          tabIndex={-1}
          autoComplete="off"
          ref={honeypotRef}
          defaultValue=""
        />
      </div>

      <button
        type="submit"
        data-testid="contact-submit"
        disabled={submitting}
        className="mt-6 inline-flex items-center justify-center gap-2 bg-mist-gold text-mist-navy px-8 h-12 text-[11px] uppercase tracking-[0.22em] font-semibold rounded-md hover:bg-[#b8994f] transition-all duration-200 ease-out disabled:opacity-70 disabled:cursor-not-allowed focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-mist-gold focus-visible:ring-offset-2"
      >
        {submitting ? (
          <>
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
            Sending…
          </>
        ) : (
          <>
            Send Message
            <Send className="h-4 w-4" aria-hidden />
          </>
        )}
      </button>
    </form>
  );
}

function FieldInput({ name, label, error, required, ...rest }) {
  const id = `field-${name}`;
  return (
    <div className="flex flex-col">
      <label htmlFor={id} className="text-[11px] uppercase tracking-[0.22em] text-mist-navy font-medium mb-2">
        {label}
        {required && <span className="text-mist-gold ml-1">*</span>}
      </label>
      <input
        id={id}
        name={name}
        data-testid={`contact-${name}`}
        aria-invalid={!!error}
        aria-describedby={error ? `${id}-error` : undefined}
        className={`h-11 px-3.5 bg-white border rounded-md text-[15px] text-mist-ink placeholder:text-mist-ink/40 transition-all duration-200 ease-out focus:outline-none focus:border-mist-gold focus:ring-2 focus:ring-mist-gold/20 ${
          error ? "border-red-400" : "border-mist-soft"
        }`}
        {...rest}
      />
      {error && (
        <span id={`${id}-error`} role="alert" className="mt-1.5 text-xs text-red-600">
          {error}
        </span>
      )}
    </div>
  );
}

function FieldTextarea({ name, label, error, required, ...rest }) {
  const id = `field-${name}`;
  return (
    <div className="flex flex-col">
      <label htmlFor={id} className="text-[11px] uppercase tracking-[0.22em] text-mist-navy font-medium mb-2">
        {label}
        {required && <span className="text-mist-gold ml-1">*</span>}
      </label>
      <textarea
        id={id}
        name={name}
        rows={6}
        data-testid={`contact-${name}`}
        aria-invalid={!!error}
        aria-describedby={error ? `${id}-error` : undefined}
        className={`px-3.5 py-3 bg-white border rounded-md text-[15px] text-mist-ink placeholder:text-mist-ink/40 transition-all duration-200 ease-out focus:outline-none focus:border-mist-gold focus:ring-2 focus:ring-mist-gold/20 resize-y ${
          error ? "border-red-400" : "border-mist-soft"
        }`}
        {...rest}
      />
      {error && (
        <span id={`${id}-error`} role="alert" className="mt-1.5 text-xs text-red-600">
          {error}
        </span>
      )}
    </div>
  );
}
